import { useState, type ReactNode } from "react";
import { Lock } from "lucide-react";
import { Input } from "../ui";

export interface PasswordFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** "new-password" when choosing one, so the browser offers to generate and save it. */
  autoComplete: "current-password" | "new-password";
  /** Shown until there's an error, e.g. "At least 8 characters". */
  hint?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Under the field on the right, e.g. "Forgot your password?". */
  extra?: ReactNode;
}

/** A password with Show/Hide inside the field (design system AuthFlow). */
export function PasswordField({ label = "Password", value, onChange, autoComplete, hint, error, disabled, autoFocus, extra }: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  const toggle = (
    <button
      type="button"
      aria-pressed={shown}
      onClick={() => setShown(!shown)}
      className="min-h-8 cursor-pointer border-none bg-transparent px-0.5 type-small font-semibold text-text-link"
    >
      {shown ? "Hide" : "Show"}
    </button>
  );
  return (
    <div className="mt-6">
      <Input
        label={label}
        type={shown ? "text" : "password"}
        icon={Lock}
        size="lg"
        autoComplete={autoComplete}
        placeholder={autoComplete === "current-password" ? "Your password" : undefined}
        value={value}
        hint={hint}
        error={error}
        disabled={disabled}
        autoFocus={autoFocus}
        suffix={toggle}
        onChange={(event) => onChange(event.target.value)}
      />
      {extra && <div className="mt-1 flex justify-end">{extra}</div>}
    </div>
  );
}
